import { getBotLoaderText, requestAbortedMsg, AG_UI_EVENT_TYPES } from '../utils/constants'
import mapper from '../../../../_endpoints'
import { useSelector } from 'react-redux'
import { useContext, useEffect, useRef } from 'react'
import { usePost } from '../../../../_hooks/usePost'
import { useDelete } from '../../../../_hooks/useDelete'
import { useFetch } from '../../../../_hooks/useFetch'
import { ChatbotContext } from '../context/ChatbotProvider'
import {
  ChatRawMessageItem,
  chatRawMessageSchema,
  ChatSubmitProps,
  FileProps,
  UploadResourceResponse,
  type McpToolConfig
} from '../utils/types'
import { useStream } from '../../../../_hooks/useStream'
import { EventSourceMessage } from '@microsoft/fetch-event-source'
import { v4 as uuid } from 'uuid'
import { toast } from 'react-toastify'
import { extractContentFromJsonStream, getMessageContent, hasImageFile } from '../utils/TextUtils'
import { base64ToBlob, applyToolConfiguration } from '../utils/helpers'
import { getChangedPersonalDataBoxes, getChangedThreadMcpServers } from '../utils/mcpServerUtils'
import { appInsights } from '../../../../_services/AppInsights'
import { EventType } from '../../../../utility/types'
import { store } from '../../../../_redux/store'
import { frontendToolRegistry } from '../frontend-tools/registry'
import { executeFrontendToolCall } from '../frontend-tools/executor'
import { getUIState } from '../frontend-tools/stateSync'
import { getActiveAgentBus } from '../frontend-tools/fdc3Bridge'
import { FrontendToolContext, PendingFrontendToolCall } from '../frontend-tools/types'
import { convertVegaLiteToChartSpec } from '../frontend-tools/tools/visualization/vegaLiteAdapter'
import { useRecentlyOpenedAppsServices } from '../../../../_services/RecentlyOpenedAppsService'

const escapeMarkdownTableCell = (value: unknown): string => {
  if (value === null || value === undefined) return ''
  return String(value).replace(/\|/g, '\\|').replace(/\r?\n/g, ' ')
}

// Maps a TOOL_TABLE_CONTENT event's tabular payload (column-typed rows) into a Markdown
// table string so it flows through the same markdown table renderer as LLM-authored tables.
const buildTableMarkdown = (table: any): string => {
  const rows: any[][] = Array.isArray(table?.data) ? table.data : []
  const columns: string[] =
    table?.resultSetMetaData?.rowType?.map((col: any) => col?.name || '') || []

  if (rows.length === 0 || columns.length === 0) return ''

  const headerRow = `| ${columns.map(escapeMarkdownTableCell).join(' | ')} |`
  const separatorRow = `| ${columns.map(() => '---').join(' | ')} |`
  const bodyRows = rows
    .map((row) => `| ${row.map(escapeMarkdownTableCell).join(' | ')} |`)
    .join('\n')
  const heading = table?.title ? `**${escapeMarkdownTableCell(table.title)}**\n\n` : ''

  return `${heading}${headerRow}\n${separatorRow}\n${bodyRows}`
}

export const useChatService = () => {
  const {
    isStreaming,
    setIsStreaming,
    chatOption,
    rawMessages,
    setRawMessages,
    currentChat,
    setCurrentChat,
    controller,
    streamReader,
    setController,
    thinkingTimeElapsed,
    setThinkingTimeElapsed,
    setIsThinking,
    selectedMessage,
    setSelectedMessage,
    pendingFrontendToolCalls,
    setPendingFrontendToolCalls,
    isWebSearchEnabled,
    selectedProject,
    projectThreadIdsRef,
    threadMcpServers,
    threadPersonalDataBoxes
  } = useContext(ChatbotContext)
  const { selectedAgent, selectedChatModel } = useSelector((state: any) => state.chatbotReducer)
  const currentChatRef = useRef<any>(null)
  const currentMessagesRef = useRef<ChatRawMessageItem[]>([])
  const thinkingStartTimeRef = useRef<number | null>(null)
  const [, sendFeedback] = usePost('nexus')
  const [, uploadResource] = usePost('nexus')
  const [, getChatStream] = useStream('nexus')
  const [, regenerateChat] = useStream('nexus')
  const [, editChatMessage] = useStream('nexus')
  const [, generateConversationTitle] = useFetch('nexus')
  const [isDeletingChat, deleteChatById] = useDelete('nexus')
  const [, getConversationTitle] = useFetch('nexus')
  const [isFetchingChatMessages, fetchChatMessagesById] = useFetch('nexus')
  const [, fetchThreadData] = useFetch('nexus')
  const [isFetchingChatHistory, fetchAllChat] = useFetch('nexus')
  const [isSearchingThreads, searchThreadsApi] = useFetch('nexus')
  const [, getApplicationById] = useFetch()
  const [isUpdatingChatTitle, renameChatTitle] = usePost('nexus')
  const { addRecentlyOpenedApp } = useRecentlyOpenedAppsServices()

  const trackChatDetails = (selectedMessage: any, threadId: string | null) => {
    const details: any = {
      messageId: selectedMessage?.id || 'aborted',
      parentId: selectedMessage?.parentId || null,
      agentId: selectedAgent?.id || null,
      threadId,
      gpt_model: selectedMessage?.modelVersion || null,
      isNewChat: String(!selectedMessage?.parentId),
      application_name: 'Ali',
      isWebSearchEnabled
    }

    if (selectedMessage?.role === 'user') {
      details.queryLength = String(selectedMessage?.content?.length || 0)
    }

    if (selectedMessage?.resources?.length > 0) {
      details.resourceIds = selectedMessage?.resources.map((r: any) => r.id).join(',')
    }

    if (details.threadId) {
      appInsights.trackEvent({
        name: EventType.ChatMessageDetails,
        properties: details
      })
    } else {
      console.error('Thread ID is missing, cannot track chat details')
    }
  }

  const createChatbotState = () =>
    getUIState({
      navigate: () => {},
      dispatch: store.dispatch,
      getState: store.getState,
      fetchApp: (nameOrId: string) =>
        getApplicationById(mapper.applications.getApplication.replace(':id', nameOrId)),
      addRecentlyOpenedApp,
      chatbotContext: {
        selectedMessage,
        setSelectedMessage,
        currentChat,
        selectedAgent
      }
    })

  const createAguiPayload = ({
    agentId,
    model,
    messages,
    threadId,
    parentMessageId,
    projectId
  }: {
    agentId: string | number | null | undefined
    model: string
    messages: any[]
    threadId?: string | null
    parentMessageId?: string | null
    projectId?: string | number | null
  }) => {
    const forwardedProps: Record<string, any> = { model }

    const aguiPayload: any = {
      method: 'agent/run',
      params: { agentId },
      body: {
        tools: frontendToolRegistry.getToolDefinitions(),
        context: [],
        forwardedProps,
        state: createChatbotState(),
        messages
      }
    }

    if (threadId) {
      aguiPayload.body.threadId = threadId
    }

    if (parentMessageId) {
      forwardedProps.parentMessageId = parentMessageId
    }

    if (projectId) {
      forwardedProps.projectId = projectId
    }

    return aguiPayload
  }

  const applyAguiForwardedProps = (
    forwardedProps: Record<string, any>,
    {
      resources = [],
      images = [],
      threadMcpServers,
      baseMcpServers,
      personalDataBoxes,
      basePersonalDataBoxes,
      selectedDataboxes = [],
      selectedDataProducts = [],
      mcpToolConfigs = []
    }: {
      resources?: FileProps[]
      images?: FileProps[]
      threadMcpServers?: any[] | null
      baseMcpServers?: any[] | null
      personalDataBoxes?: Array<{ personalDataBoxId: string; resourceIds: string[] }> | null
      basePersonalDataBoxes?: Array<{ personalDataBoxId: string; resourceIds: string[] }> | null
      selectedDataboxes?: Array<{
        id: string
        name: string
        smartDocsDataboxId?: string
      }>
      selectedDataProducts?: Array<{ id: string; name: string; teamIdentifier?: string }>
      mcpToolConfigs?: McpToolConfig[]
    }
  ) => {
    const changedThreadMcpServers = getChangedThreadMcpServers(threadMcpServers, baseMcpServers)
    const changedPersonalDataBoxes = getChangedPersonalDataBoxes(
      personalDataBoxes,
      basePersonalDataBoxes
    )

    if (changedThreadMcpServers.length > 0) {
      forwardedProps.threadMcpServers = changedThreadMcpServers
    }

    if (changedPersonalDataBoxes.length > 0) {
      forwardedProps.personalDataBoxes = changedPersonalDataBoxes
    }

    if (resources && resources.length > 0) {
      forwardedProps.resourceIds = resources.map((file: FileProps) => file.id)
    }

    if (images && images.length > 0) {
      forwardedProps.imageIds = images.map((img: FileProps) => img.id)
    }

    if (selectedDataboxes && selectedDataboxes.length > 0) {
      forwardedProps.selectedDataboxes = selectedDataboxes
    }

    if (selectedDataProducts && selectedDataProducts.length > 0) {
      forwardedProps.selectedDataProducts = selectedDataProducts
    }

    applyToolConfiguration(forwardedProps, mcpToolConfigs)
  }

  //local variables
  const streamDisplayTextRef = useRef<string>('')
  const rawJsonBufferRef = useRef<string>('')
  const currentTempParentIdRef = useRef<string | null>(null)
  const currentReasoningContentRef = useRef<string>('')
  const pendingToolReasoningRef = useRef<string>('')
  const isCurrentMessageReasoningRef = useRef<boolean | null>(null)
  const markerBuffer = ''
  const currentToolCalls: Map<string, any> = new Map()
  // Tables persist across TEXT_MESSAGE_START resets, since tool tables usually arrive before the closing text.
  const toolTableMarkdownBlocksRef = useRef<string[]>([])

  // Recombines any tool table(s) received this run with the current text content.
  const composeAssistantContent = (textContent: string): string =>
    toolTableMarkdownBlocksRef.current.length > 0
      ? [...toolTableMarkdownBlocksRef.current, textContent].filter(Boolean).join('\n\n')
      : textContent

  useEffect(() => {
    currentChatRef.current = currentChat
  }, [currentChat])

  useEffect(() => {
    currentMessagesRef.current = rawMessages
  }, [rawMessages])

  const createConversationTitle = async (threadId: string) => {
    const response = await generateConversationTitle(
      mapper.nexus.generateConversationTitle.replace(':id', threadId)
    )
    if (response?.thread?.name) {
      setCurrentChat({
        id: threadId,
        title: response.thread.name,
        recentlyUpdated: true,
        agentId: selectedAgent?.id || null
      })
    } else {
      toast.error(response?.message || 'Failed to generate conversation title')
    }
  }

  const getChatMessages = async (offset: number, limit: number, threadId?: any) => {
    const response = await fetchChatMessagesById(
      `${mapper.nexus.getChatMessages.replace(
        ':id',
        threadId || currentChat?.id
      )}?offset=${offset}&limit=${limit}`
    )
    return response
  }

  const getThreadDetails = async (chatId: string) => {
    const response = await fetchThreadData(mapper.nexus.getThreadDetails.replace(':id', chatId))
    return response
  }

  const getChatHistory = async (offset: number, limit: number) => {
    const response = await fetchAllChat(
      `${mapper.nexus.getConversationList}?offset=${offset}&limit=${limit}`
    )

    return response
  }

  const searchThreads = async (query: string) => {
    const response = await searchThreadsApi(
      `${mapper.nexus.searchThreads}?query=${encodeURIComponent(query)}`
    )
    return response
  }

  const deleteChat = async (chatId: string) => {
    return await deleteChatById({ url: mapper.nexus.updateConversation.replace(':id', chatId) })
  }

  const getConversationName = async (chatId: string) => {
    return await getConversationTitle(mapper.nexus.updateConversation.replace(':id', chatId))
  }

  const updateChatTitle = async (chatId: string, title: string) => {
    renameChatTitle({
      url: `${mapper.nexus.updateConversation.replace(':id', chatId)}`,
      data: {
        name: title
      },
      method: 'put'
    })
  }

  const sendMessageFeedback = async ({
    isPositive,
    isConsentGiven,
    text,
    threadId,
    messageId
  }: any) => {
    const data = {
      isPositive,
      isConsentGiven,
      text,
      threadId,
      messageId
    }
    return await sendFeedback({
      url: mapper.nexus.sendFeedback,
      data,
      abortSignal: controller?.signal
    })
  }

  const uploadResourceFile = async (file: File): Promise<UploadResourceResponse> => {
    const formData = new FormData()
    formData.append('file', file)
    // Opt into server-side dedup: identical files return the existing resource.
    formData.append('ReuseExisting', 'true')

    return await uploadResource({
      url: mapper.nexus.uploadResource,
      data: formData,
      hasFiles: true,
      abortSignal: controller?.signal
    })
  }

  // Upload multiple chat images. Accepts array of objects with either File or {name, content: url}
  const uploadChatImages = async (images: any[]) => {
    try {
      const formData = new FormData()
      for (const img of images) {
        // convert base64 to blob using shared helper
        const blob = base64ToBlob(img.content)
        if (blob) {
          const file = new File([blob], img.name || `image_${Date.now()}.png`, {
            type: blob.type
          })
          formData.append('images', file)
        } else {
          console.warn('Failed to convert base64 to blob for image', img.name)
        }
      }

      const response: any = await uploadResource({
        url: '/api/chat/images/upload',
        data: formData,
        hasFiles: true,
        abortSignal: controller?.signal
      })

      // Return raw response from the upload endpoint (expected to be array of { imageId, sasUrl })
      return response
    } catch (err: any) {
      console.error('uploadChatImages error', err)
      return { error: err?.message || 'Image upload failed' }
    }
  }

  // unused code, will remove in future
  // const setTextFromChunk = (chunk: string) => {
  //   markerBuffer += chunk

  //   try {
  //     // First, try to parse as complete JSON to avoid regex altogether
  //     try {
  //       const parsed = JSON.parse(markerBuffer)
  //       if (parsed.content) {
  //         return parsed.content
  //       }
  //     } catch {
  //       // Not complete JSON, continue with regex parsing
  //     }

  //     // Optimized regex with possessive quantifiers to prevent backtracking
  //     const completeContentMatch = markerBuffer.match(/"content"\s*:\s*"((?:[^"\\]|\\.)*)"/)
  //     if (completeContentMatch) {
  //       return completeContentMatch[1]
  //         .replace(/\\n/g, '\n')
  //         .replace(/\\"/g, '"')
  //         .replace(/\\\\/g, '\\')
  //     }

  //     // For streaming case, look for content start and extract until end of buffer
  //     const streamingMatch = markerBuffer.match(/"content"\s*:\s*"((?:[^"\\]|\\.)*?)(?:"|\s*$)/)
  //     if (streamingMatch && !streamingMatch[0].endsWith('"')) {
  //       return streamingMatch[1].replace(/\\n/g, '\n').replace(/\\"/g, '"').replace(/\\\\/g, '\\')
  //     }

  //     return ''
  //   } catch (error) {
  //     console.error('Error parsing chunk for content:', error)
  //     return ''
  //   }
  // }

  const userRespRef = useRef<ChatRawMessageItem>({} as ChatRawMessageItem)
  const assistantRespRef = useRef<ChatRawMessageItem>({} as ChatRawMessageItem)

  const handleStreamMessage = ({
    event,
    currentChatItem,
    currentMessages,
    isRegenerate = false
  }: {
    event: EventSourceMessage
    currentChatItem: ChatRawMessageItem
    currentMessages: ChatRawMessageItem[]
    isRegenerate?: boolean
  }) => {
    const eventData = JSON.parse(event.data)
    const eventType = eventData.type

    // Emits the current accumulated state as the in-progress assistant message.
    // Always uses currentChatItem as base so parentId/id stay correct for message grouping.
    const publishUpdate = () =>
      setRawMessages([
        ...currentMessages,
        {
          ...currentChatItem,
          content: assistantRespRef.current.content || '',
          reasoningContent: assistantRespRef.current.reasoningContent || '',
          toolCalls: assistantRespRef.current.toolCalls || [],
          reasonings: assistantRespRef.current.reasonings || []
        }
      ])

    switch (eventType) {
      case AG_UI_EVENT_TYPES.RUN_STARTED: {
        // Initialize run tracking
        const currentRunId = eventData.runId
        const threadId = eventData.threadId
        toolTableMarkdownBlocksRef.current = []

        const currentMessagesCopy: ChatRawMessageItem[] = JSON.parse(
          JSON.stringify(currentMessages)
        )
        assistantRespRef.current = JSON.parse(JSON.stringify(currentChatItem)) as ChatRawMessageItem
        userRespRef.current =
          [...currentMessagesCopy].findLast(
            (msg) => msg.id === assistantRespRef.current.parentId && msg.role === 'user'
          ) || ({} as ChatRawMessageItem)

        if (currentRunId) {
          assistantRespRef.current.runId = currentRunId
          if (userRespRef.current) userRespRef.current.runId = currentRunId
        }
        if (threadId) {
          const currThreadID = currentChat.id
          if (currThreadID === null) {
            setCurrentChat({
              ...currentChat,
              id: threadId
            })
            createConversationTitle(threadId)
          }
        }

        const userId = eventData.input?.messages?.[0]?.id
        if (userRespRef.current && userId) {
          userRespRef.current.id = assistantRespRef.current.parentId = userId
        }

        //track user msg only for new messages, not regeneration
        if (!isRegenerate) {
          trackChatDetails(userRespRef.current, eventData.threadId)
        }

        // Start thinking timer
        thinkingStartTimeRef.current = Date.now()
        setIsThinking(true)

        break
      }

      case AG_UI_EVENT_TYPES.TEXT_MESSAGE_START:
        // Defer stopping the thinking timer until we know if this message is reasoning-only
        streamDisplayTextRef.current = ''
        rawJsonBufferRef.current = ''
        isCurrentMessageReasoningRef.current = null
        assistantRespRef.current.id = eventData.messageId

        // Store temp parent ID before overwriting
        if (currentChatItem.parentId?.startsWith('temp-')) {
          currentTempParentIdRef.current = currentChatItem.parentId
        }
        break

      case AG_UI_EVENT_TYPES.TEXT_MESSAGE_CONTENT: {
        // Accumulate text content
        const delta = eventData.delta || ''
        rawJsonBufferRef.current += delta

        // Some responses stream the complete response envelope as JSON. Keep any fields that
        // are already complete so the chat UI can render them before RUN_FINISHED.
        try {
          const streamedResponse = JSON.parse(rawJsonBufferRef.current)
          if (streamedResponse && typeof streamedResponse === 'object') {
            if (Array.isArray(streamedResponse.citations)) {
              assistantRespRef.current.citations = streamedResponse.citations
            }
            if (Array.isArray(streamedResponse.toolCalls)) {
              assistantRespRef.current.toolCalls = streamedResponse.toolCalls
              streamedResponse.toolCalls.forEach((toolCall: any) => {
                if (toolCall?.id) currentToolCalls.set(toolCall.id, toolCall)
              })
            }
            if (typeof streamedResponse.reasoningContent === 'string') {
              assistantRespRef.current.reasoningContent = streamedResponse.reasoningContent
            }
            if (Array.isArray(streamedResponse.reasonings)) {
              assistantRespRef.current.reasonings = streamedResponse.reasonings
            }
          }
        } catch {
          // The JSON envelope is incomplete while it is streaming.
        }

        // Extract content from JSON wrapper if present, otherwise use raw text
        const extracted = extractContentFromJsonStream(rawJsonBufferRef.current)
        const rawText = (extracted ?? rawJsonBufferRef.current).trimStart()

        // Determine if this message is reasoning-only content (independent of thinking timer state).
        if (
          isCurrentMessageReasoningRef.current === null &&
          rawText.length >= 'Reasoning: '.length
        ) {
          isCurrentMessageReasoningRef.current = /^Reasoning:\s/i.test(rawText)
          // Stop thinking timer only when content is confirmed NOT to be reasoning
          if (thinkingStartTimeRef.current && !isCurrentMessageReasoningRef.current) {
            const elapsed = Math.round((Date.now() - thinkingStartTimeRef.current) / 1000)
            setThinkingTimeElapsed(elapsed)
            assistantRespRef.current.elapsedTime = elapsed
            thinkingStartTimeRef.current = null
            setIsThinking(false)
          }
        }

        if (isCurrentMessageReasoningRef.current) {
          // Strip the "Reasoning: " prefix and store as reasoning content.
          // Also clear any partial content that may have been set before the determination was made.
          assistantRespRef.current.content = composeAssistantContent('')
          streamDisplayTextRef.current = ''
          assistantRespRef.current.reasoningContent = rawText.replace(/^Reasoning:\s*/i, '')
        } else {
          streamDisplayTextRef.current = rawText
          assistantRespRef.current.content = composeAssistantContent(streamDisplayTextRef.current)
        }
        publishUpdate()
        break
      }

      case AG_UI_EVENT_TYPES.REASONING_START:
        // Reset reasoning buffer for this message
        currentReasoningContentRef.current = ''
        pendingToolReasoningRef.current = ''
        assistantRespRef.current.reasoningContent = ''
        break

      case AG_UI_EVENT_TYPES.REASONING_CONTENT: {
        // Accumulate reasoning delta, stripping any leading "Reasoning:" prefix
        const reasoningDelta = eventData.delta || ''
        currentReasoningContentRef.current += reasoningDelta
        pendingToolReasoningRef.current += reasoningDelta
        assistantRespRef.current.reasoningContent = currentReasoningContentRef.current.replace(
          /^Reasoning:\s*/i,
          ''
        )
        publishUpdate()
        break
      }

      case AG_UI_EVENT_TYPES.REASONING_END:
        // Reasoning stream complete - final value already in assistantRespRef.current.reasoningContent
        break

      case AG_UI_EVENT_TYPES.TOOL_CALL_START: {
        // Start tracking tool call
        const toolCallId = eventData.toolCallId
        const toolCallName = eventData.toolCallName
        const parentMessageId = eventData.parentMessageId

        currentToolCalls.set(toolCallId, {
          id: toolCallId,
          name: toolCallName,
          arguments: '',
          parentMessageId,
          reasoning:
            eventData.reasoning || eventData.toolCallReasoning || pendingToolReasoningRef.current,
          isFrontend: eventData.toolCallSource === 'frontend'
        })
        pendingToolReasoningRef.current = ''

        assistantRespRef.current.toolCalls = Array.from(currentToolCalls.values())
        publishUpdate()
        break
      }

      case AG_UI_EVENT_TYPES.TOOL_CALL_ARGS: {
        // Accumulate tool call arguments
        const argDelta = eventData.delta || ''
        const argsToolCallId = eventData.toolCallId

        if (currentToolCalls.has(argsToolCallId)) {
          const toolCall = currentToolCalls.get(argsToolCallId)
          toolCall.arguments += argDelta
          currentToolCalls.set(argsToolCallId, toolCall)

          // Update UI with streaming arguments - update last message in place
          assistantRespRef.current.toolCalls = Array.from(currentToolCalls.values())
          publishUpdate()
        }
        break
      }

      case 'TOOL_CALL_REASONING_START':
      case 'TOOL_CALL_REASONING_CONTENT':
      case 'TOOL_CALL_REASONING': {
        const reasoningToolCallId = eventData.toolCallId
        const reasoningToolCall = currentToolCalls.get(reasoningToolCallId)

        if (reasoningToolCall) {
          if (eventType === 'TOOL_CALL_REASONING_START') reasoningToolCall.reasoning = ''
          reasoningToolCall.reasoning =
            (reasoningToolCall.reasoning || '') + (eventData.delta || eventData.reasoning || '')
          currentToolCalls.set(reasoningToolCallId, reasoningToolCall)
          assistantRespRef.current.toolCalls = Array.from(currentToolCalls.values())
          publishUpdate()
        }
        break
      }

      case AG_UI_EVENT_TYPES.TOOL_CALL_END: {
        const endToolCallId = eventData.toolCallId
        const endToolCall = currentToolCalls.get(endToolCallId)

        if (endToolCall) {
          endToolCall.ended = true
          endToolCall.isFrontend = frontendToolRegistry.has(endToolCall.name)
          currentToolCalls.set(endToolCallId, endToolCall)
          assistantRespRef.current.toolCalls = Array.from(currentToolCalls.values())
          publishUpdate()
        }

        if (endToolCall && frontendToolRegistry.has(endToolCall.name)) {
          // Build context from available closures
          const toolContext: FrontendToolContext = {
            navigate: () => {},
            dispatch: store.dispatch,
            getState: store.getState,
            fetchApp: (nameOrId: string) =>
              getApplicationById(mapper.applications.getApplication.replace(':id', nameOrId)),
            addRecentlyOpenedApp,
            chatbotContext: {
              selectedMessage,
              setSelectedMessage,
              currentChat,
              selectedAgent
            },
            abortSignal: controller?.signal
          }

          // Execute async — don't block the stream (RQ9)
          executeFrontendToolCall(
            endToolCall,
            toolContext,
            pendingFrontendToolCalls,
            setPendingFrontendToolCalls
          ).then((result) => {
            endToolCall.result = JSON.stringify(result)
            assistantRespRef.current.toolCalls = Array.from(currentToolCalls.values())
            publishUpdate()
          })
        }
        break
      }

      case AG_UI_EVENT_TYPES.TOOL_CALL_RESULT: {
        // Store tool call result
        const resultToolCallId = eventData.toolCallId
        const result = eventData.result

        if (currentToolCalls.has(resultToolCallId)) {
          const toolCall = currentToolCalls.get(resultToolCallId)
          toolCall.result = result
          currentToolCalls.set(resultToolCallId, toolCall)

          // Update UI with tool call result - update last message in place
          assistantRespRef.current.toolCalls = Array.from(currentToolCalls.values())
          publishUpdate()
        }
        break
      }

      // snowflake agent table
      case 'TOOL_TABLE_CONTENT': {
        // Title arrives as a sibling of `table`, not inside it.
        const tableMarkdown = buildTableMarkdown({ ...eventData.table, title: eventData.title })
        if (tableMarkdown) {
          toolTableMarkdownBlocksRef.current = [
            ...toolTableMarkdownBlocksRef.current,
            tableMarkdown
          ]
          assistantRespRef.current.content = composeAssistantContent(streamDisplayTextRef.current)
          publishUpdate()
        }
        break
      }

      // snowflake agent chart
      case 'TOOL_CHART_CONTENT': {
        // Synthesize a `create_visualization` tool call to reuse ChatBubble's existing chart rendering.
        const chartSpec = convertVegaLiteToChartSpec(eventData.chart)
        if (chartSpec) {
          const chartToolCallId = eventData.toolCallId || `chart-${uuid()}`
          currentToolCalls.set(chartToolCallId, {
            id: chartToolCallId,
            name: 'create_visualization',
            arguments: JSON.stringify(chartSpec),
            ended: true
          })
          assistantRespRef.current.toolCalls = Array.from(currentToolCalls.values())
          publishUpdate()
        }
        break
      }

      case AG_UI_EVENT_TYPES.RUN_FINISHED: {
        // Extract citations from the accumulated JSON buffer
        if (rawJsonBufferRef.current) {
          const { content, citations, toolCalls, reasoningContent, reasonings } = getMessageContent(
            rawJsonBufferRef.current
          )
          if (content) assistantRespRef.current.content = composeAssistantContent(content)
          assistantRespRef.current.citations = citations || []
          if (toolCalls) assistantRespRef.current.toolCalls = toolCalls
          if (reasoningContent) assistantRespRef.current.reasoningContent = reasoningContent
          if (reasonings) assistantRespRef.current.reasonings = reasonings
        }

        const messagesCopy: ChatRawMessageItem[] = [...rawMessages]
        const grandParentMessage: ChatRawMessageItem = JSON.parse(
          JSON.stringify(messagesCopy.pop() || {})
        )

        //Assigning the parentId to the previous user message/prompt
        if (userRespRef.current && Object.keys(userRespRef.current).length > 0) {
          userRespRef.current.childrenIds.push(assistantRespRef.current.id || '')
          // parentMessage.runId = parentMessage.runId || uuid()
        }

        //Adding childrenIds to the grandParentMessage
        if (Object.keys(grandParentMessage).length > 0) {
          grandParentMessage.childrenIds.push(userRespRef.current?.id || '')
        }
        // Ensure elapsedTime is persisted on the assistant response
        if (!assistantRespRef.current.elapsedTime && thinkingStartTimeRef.current) {
          assistantRespRef.current.elapsedTime = Math.round(
            (Date.now() - thinkingStartTimeRef.current) / 1000
          )
          thinkingStartTimeRef.current = null
        }

        const userRespExistsInHistory = !!(
          userRespRef.current?.id && messagesCopy.some((msg) => msg.id === userRespRef.current.id)
        )
        const resolvedMessages = userRespExistsInHistory
          ? messagesCopy.map((msg) =>
              msg.id === userRespRef.current.id
                ? { ...msg, childrenIds: userRespRef.current.childrenIds }
                : msg
            )
          : messagesCopy

        //track assistant resp
        trackChatDetails(assistantRespRef.current, eventData.threadId)

        if (Object.keys(grandParentMessage).length > 0) {
          setRawMessages([
            ...resolvedMessages,
            grandParentMessage,
            ...(userRespExistsInHistory ? [] : [userRespRef.current]),
            assistantRespRef.current
          ])
        } else {
          setRawMessages([
            ...resolvedMessages,
            ...(userRespExistsInHistory ? [] : [userRespRef.current]),
            assistantRespRef.current
          ])
        }
        setIsStreaming(false)
        setThinkingTimeElapsed(0)
        break
      }

      case AG_UI_EVENT_TYPES.RUN_ERROR: {
        const errorMessage = eventData.message || 'Something went wrong. Please try again.'
        console.error('RUN_ERROR received:', errorMessage)

        // Stop thinking/streaming spinners
        if (thinkingStartTimeRef.current) {
          thinkingStartTimeRef.current = null
        }
        setIsThinking(false)
        setThinkingTimeElapsed(0)

        // Show error in the assistant bubble
        if (!assistantRespRef.current || Object.keys(assistantRespRef.current).length === 0) {
          assistantRespRef.current = JSON.parse(
            JSON.stringify(currentChatItem)
          ) as ChatRawMessageItem
        }
        assistantRespRef.current.content = `⚠️ ${errorMessage}`
        assistantRespRef.current.id = assistantRespRef.current.id || `error-${uuid()}`

        // Commit the error message to the chat
        const errorMessages = currentMessages.filter((msg) => msg.type !== 'loader')
        setRawMessages([...errorMessages, assistantRespRef.current])
        setIsStreaming(false)
        break
      }

      default:
        // Handle any unknown event types or legacy format
        console.warn('Unknown event type:', eventType, eventData)
        break
    }
  }

  const onChatStream = async ({
    prompt,
    currentMessages,
    parentId,
    userTempId,
    resources = [],
    images = [],
    threadMcpServers,
    mcpToolConfigs,
    selectedDataboxes = [],
    selectedDataProducts = [],
    personalDataBoxes
  }: {
    prompt: string
    currentMessages: ChatRawMessageItem[]
    parentId?: string | null
    userTempId: string
    resources?: FileProps[]
    images?: FileProps[]
    threadMcpServers?: any[]
    mcpToolConfigs?: McpToolConfig[]
    selectedDataboxes?: Array<{ id: string; name: string; smartDocsDataboxId?: string }>
    selectedDataProducts?: Array<{ id: string; name: string }>
    personalDataBoxes?: Array<{ personalDataBoxId: string; resourceIds: string[] }>
  }) => {
    const threadId = currentChat.id
    const currentChatItem: ChatRawMessageItem = JSON.parse(JSON.stringify(chatRawMessageSchema))
    currentChatItem.parentId = userTempId
    currentChatItem.modelVersion = selectedChatModel?.name || ''
    setIsStreaming(true)
    const abortSignal = controller?.signal

    // Reset the per-run FDC3 action cap (Spec 010 §10.2 loop guard); no-op off the bus.
    getActiveAgentBus()?.resetRunGuard()

    const aguiPayload: any = createAguiPayload({
      agentId: selectedAgent?.id,
      model: selectedChatModel?.name || 'gpt-5.5',
      messages: [{ role: 'user', content: prompt }],
      threadId,
      parentMessageId: parentId,
      projectId:
        selectedProject?.id ??
        (threadId ? projectThreadIdsRef?.current?.get(threadId)?.id : undefined)
    })

    applyAguiForwardedProps(aguiPayload.body.forwardedProps, {
      resources,
      images,
      threadMcpServers,
      baseMcpServers: selectedAgent?.agentMcpServers,
      selectedDataboxes,
      selectedDataProducts,
      personalDataBoxes,
      basePersonalDataBoxes: selectedAgent?.personalDataBoxes,
      mcpToolConfigs
    })

    const error = await getChatStream({
      url: mapper.nexus.chatStream,
      data: aguiPayload,
      abortSignal,
      onmessage: (event: EventSourceMessage) => {
        handleStreamMessage({
          event,
          currentChatItem,
          currentMessages
        })
      }
    })
    if (error) {
      setIsStreaming(false)
      toast.error(`Error while streaming: ${error?.message}`)
      setRawMessages(rawMessages)
    }
  }

  const onEditPrompt = async ({
    prompt,
    currentMessages,
    userTempId,
    messageId,
    resources = [],
    images = [],
    mcpToolConfigs
  }: {
    prompt: string
    currentMessages: ChatRawMessageItem[]
    userTempId: string
    messageId: string
    resources?: FileProps[]
    images?: FileProps[]
    mcpToolConfigs?: any[]
  }) => {
    const currentChatItem: ChatRawMessageItem = JSON.parse(JSON.stringify(chatRawMessageSchema))
    currentChatItem.parentId = userTempId
    currentChatItem.modelVersion = selectedChatModel?.name || ''
    setIsStreaming(true)
    const abortSignal = controller?.signal

    const aguiPayload: any = createAguiPayload({
      agentId: selectedAgent?.id,
      model: selectedChatModel?.name || 'gpt-5.5',
      messages: [{ id: messageId, role: 'user', content: prompt }]
    })

    applyAguiForwardedProps(aguiPayload.body.forwardedProps, {
      resources,
      images,
      threadMcpServers,
      baseMcpServers: selectedAgent?.agentMcpServers,
      personalDataBoxes: threadPersonalDataBoxes,
      basePersonalDataBoxes: selectedAgent?.personalDataBoxes,
      mcpToolConfigs
    })

    const error = await editChatMessage({
      url: mapper.nexus.regenerateResponse,
      data: aguiPayload,
      abortSignal,
      onmessage: (event: EventSourceMessage) => {
        handleStreamMessage({
          event,
          currentChatItem,
          currentMessages
        })
      }
    })
    if (error) {
      setIsStreaming(false)
      toast.error(`Error while streaming: ${error?.message}`)
      setRawMessages(rawMessages)
    }
  }

  const onRegenerateResponse = async (
    parentId: string,
    messageId: string,
    agentId: string,
    model: string
  ) => {
    setThinkingTimeElapsed(0)
    setIsStreaming(true)
    const currentChatItem: ChatRawMessageItem = JSON.parse(JSON.stringify(chatRawMessageSchema))
    currentChatItem.parentId = parentId
    currentChatItem.modelVersion = model || selectedChatModel?.name || ''
    const messagesCopy = [...rawMessages]
    initiateLoader(rawMessages, parentId)
    const abortSignal = controller?.signal

    const aguiPayload: any = createAguiPayload({
      agentId,
      model: model || selectedChatModel?.name || 'gpt-5.5',
      messages: [{ id: messageId, role: 'assistant' }],
      threadId: currentChat?.id
    })

    applyAguiForwardedProps(aguiPayload.body.forwardedProps, {
      threadMcpServers,
      baseMcpServers: selectedAgent?.agentMcpServers,
      personalDataBoxes: threadPersonalDataBoxes,
      basePersonalDataBoxes: selectedAgent?.personalDataBoxes
    })

    const error = await regenerateChat({
      url: mapper.nexus.regenerateResponse,
      data: aguiPayload,
      abortSignal,
      onmessage: (event: EventSourceMessage) => {
        handleStreamMessage({
          event,
          currentChatItem,
          currentMessages: messagesCopy,
          isRegenerate: true
        })
      }
    })
    if (error) {
      setIsStreaming(false)
      toast.error(`Error while streaming: ${error?.message}`)
      setRawMessages(rawMessages)
    }
  }

  const initiateLoader = (currMessages: ChatRawMessageItem[], parentId: string | null) => {
    const loaderMsg: ChatRawMessageItem = {
      ...chatRawMessageSchema,
      parentId,
      content: getBotLoaderText(),
      type: 'loader'
    }
    setRawMessages([...currMessages, loaderMsg])
  }

  const handleChatSubmit = async ({
    prompt,
    parentId,
    isEditPrompt = false,
    messageId = '',
    files,
    images,
    threadMcpServers,
    mcpToolConfigs,
    selectedDataboxes,
    selectedDataProducts,
    personalDataBoxes
  }: ChatSubmitProps) => {
    setThinkingTimeElapsed(0)
    setIsStreaming(true)
    // Keep interrupted/temp user turns in the live thread so they remain visible before refresh.
    // Only strip transient loader rows from the previous request.
    const currentMessages: ChatRawMessageItem[] = [
      ...rawMessages.filter((msg) => msg.type !== 'loader')
    ]

    const tempId = `temp-${uuid()}`
    const resources = files || []

    const chatItem: ChatRawMessageItem = {
      ...chatRawMessageSchema,
      parentId,
      id: tempId,
      agentId: selectedAgent?.id,
      content: prompt,
      ...(resources?.length > 0 && { resources }),
      ...(images?.length > 0 && { images }),
      modelVersion: selectedChatModel ? selectedChatModel.name : '',
      role: 'user'
    }

    currentMessages.push(chatItem)
    initiateLoader(currentMessages, tempId)
    if (isEditPrompt) {
      onEditPrompt({
        prompt,
        currentMessages,
        userTempId: tempId,
        messageId,
        resources,
        images,
        mcpToolConfigs
      })
    } else {
      onChatStream({
        prompt,
        currentMessages,
        parentId,
        userTempId: tempId,
        resources,
        images,
        ...(mcpToolConfigs && { mcpToolConfigs }),
        ...(threadMcpServers && threadMcpServers?.length && { threadMcpServers }),
        ...(selectedDataboxes && { selectedDataboxes }),
        ...(selectedDataProducts && { selectedDataProducts }),
        ...(personalDataBoxes && personalDataBoxes.length > 0 && { personalDataBoxes })
      })
    }
  }

  const abortRequest = async (shouldUpdateMessage: boolean) => {
    setIsStreaming(false)
    // Without this, aborting mid-"thinking" (e.g. switching chats before any content streams in)
    // leaves isThinking stuck true, so the loader keeps showing on whichever chat is opened next.
    setIsThinking(false)
    setThinkingTimeElapsed(0)
    thinkingStartTimeRef.current = null
    streamReader.current = null
    const cloneMessages: ChatRawMessageItem[] = [...rawMessages]

    const loaderMsg = cloneMessages.find((msg) => msg.type === 'loader')
    const messagesWithoutLoader = cloneMessages.filter((msg) => msg.type !== 'loader')

    if (!shouldUpdateMessage) {
      setRawMessages(messagesWithoutLoader)
      return
    }

    // Prefer updating the latest assistant response for the interrupted request.
    const targetAssistantIndex = [...messagesWithoutLoader]
      .map((msg, index) => ({ msg, index }))
      .filter(
        ({ msg }) =>
          msg.role === 'assistant' &&
          msg.type === 'text' &&
          (!loaderMsg?.parentId || msg.parentId === loaderMsg.parentId)
      )
      .map(({ index }) => index)
      .pop()

    if (targetAssistantIndex !== undefined) {
      const targetMessage = messagesWithoutLoader[targetAssistantIndex]
      const existingContent = (targetMessage.content || '').trim()
      const contentWithAbortNote =
        existingContent.length > 0 ? targetMessage.content : requestAbortedMsg

      const updatedMessage = {
        ...targetMessage,
        id: targetMessage.id || `temp-${uuid()}`,
        content: contentWithAbortNote
      }

      messagesWithoutLoader[targetAssistantIndex] = updatedMessage

      // Extract data for tracking only - don't modify the message
      const userMessage = messagesWithoutLoader.find((msg) => msg.id === targetMessage.parentId)
      const trackingData = {
        ...updatedMessage,
        parentId: targetMessage.parentId,
        modelVersion:
          targetMessage.modelVersion || userMessage?.modelVersion || selectedChatModel?.name || ''
      }

      //track aborted msg
      trackChatDetails(trackingData, currentChatRef.current?.id || null)

      setRawMessages(messagesWithoutLoader)
      return
    }

    // If no assistant message exists yet, create one so the interrupted query is still visible.
    if (loaderMsg) {
      const abortedMessage: ChatRawMessageItem = {
        ...loaderMsg,
        id: loaderMsg.id || `temp-${uuid()}`,
        type: 'text' as const,
        content: requestAbortedMsg
      }

      // Extract data for tracking only - don't modify the message
      const userMessage = messagesWithoutLoader.find((msg) => msg.id === loaderMsg.parentId)
      const trackingData = {
        ...abortedMessage,
        parentId: loaderMsg.parentId,
        modelVersion:
          userMessage?.modelVersion || loaderMsg.modelVersion || selectedChatModel?.name || ''
      }

      //track aborted msg
      trackChatDetails(trackingData, currentChatRef.current?.id || null)

      setRawMessages([...messagesWithoutLoader, abortedMessage])
      return
    }

    setRawMessages(messagesWithoutLoader)
  }

  const cancelAPICall = async (shouldUpdateMessage = true) => {
    try {
      if (streamReader.current) streamReader.current.cancel()
      setController((prevController: any) => {
        if (prevController) {
          prevController.abort()
        }
        return new AbortController()
      })
    } catch (err) {
      console.error('Error while aborting request:', err)
    } finally {
      if (isStreaming) await abortRequest(shouldUpdateMessage)
    }
  }

  return {
    handleChatSubmit,
    isDeletingChat,
    deleteChat,
    isUpdatingChatTitle,
    updateChatTitle,
    getConversationName,
    getChatMessages,
    isFetchingChatMessages,
    getChatHistory,
    isFetchingChatHistory,
    searchThreads,
    isSearchingThreads,
    cancelAPICall,
    onRegenerateResponse,
    uploadResourceFile,
    uploadChatImages,
    sendMessageFeedback,
    getThreadDetails
  }
}
